import React, { useEffect, useRef, useState } from 'react';
import { DesignFloorPlan, FloorPlanPoint, Wall } from '../../../types';
import { useLiveFloorPlanStore } from '../../../store/liveFloorPlanStore';
import { useProjectStore } from '../../../store/projectStore';
import { clientToCanvas, getPanningLayer } from '../../../utils/canvasDrag';
import {
  DEFAULT_PIXELS_PER_METER,
  doorSwingGeometry,
  formatMeters,
  formatMetersValue,
  getFloorPlanReferenceImages,
  openingEndpoints,
  polygonCentroid,
  roomAreaMeters,
  wallLengthMeters,
  wallLabelOffset,
  wallLabelOffsetFromPoint,
  wallLabelPosition,
  clusterVertices,
  wallResizeCursor,
  cornerResizeCursor,
  DoorHinge,
  DoorSwingSide,
} from '../../../utils/floorPlanGeometry';
import { isVisibleOnCanvas } from '../../../utils/browserVisibility';

interface DoorPreview {
  wallId: string;
  t: number;
  widthM: number;
  fits: boolean;
  hinge: DoorHinge;
  swingSide: DoorSwingSide;
}

interface FloorPlanLayerProps {
  floorPlan: DesignFloorPlan;
  selectedWallId: string | null;
  selectedWallIds?: string[];
  selectedRoomId: string | null;
  selectedOpeningId: string | null;
  selectedReferenceImageId?: string | null;
  /** When true (Select mode), reference images accept double-click / transform. */
  referenceImagesInteractive?: boolean;
  liveReferenceGeometry?: {
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  onReferenceImageDoubleClick?: (imageId: string) => void;
  onReferenceImageMoveStart?: (imageId: string, event: React.PointerEvent) => void;
  onReferenceResizeStart?: (
    imageId: string,
    corner: 'nw' | 'ne' | 'sw' | 'se',
    event: React.PointerEvent
  ) => void;
  /** Closed loop under cursor that is not yet a named room */
  hoverRoomPreview?: { polygon: FloorPlanPoint[]; wallIds: string[] } | null;
  /** Live door placement preview while in door mode */
  doorPreview?: DoorPreview | null;
  onSelectWall?: (wallId: string, event?: React.MouseEvent) => void;
  onSelectRoom?: (roomId: string) => void;
  onSelectOpening?: (openingId: string) => void;
  onRenameRoom?: (roomId: string) => void;
  onWallPointerDown?: (
    event: React.PointerEvent,
    info: { mode: 'push' | 'corner'; wallId: string; vertexPoint?: FloorPlanPoint }
  ) => void;
  onCommitWallLength?: (wallId: string, raw: string) => boolean;
  showHandles?: boolean;
  interactive?: boolean;
}

const WallLengthEditor: React.FC<{
  value: string;
  unit: string;
  onCommit: (raw: string) => void;
  onCancel: () => void;
}> = ({ value, unit, onCommit, onCancel }) => {
  const [draft, setDraft] = useState(value);
  const rootRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef(draft);
  const closedRef = useRef(false);
  const onCommitRef = useRef(onCommit);
  const onCancelRef = useRef(onCancel);
  draftRef.current = draft;
  onCommitRef.current = onCommit;
  onCancelRef.current = onCancel;

  const finish = (commit: boolean) => {
    if (closedRef.current) return;
    closedRef.current = true;
    if (commit) onCommitRef.current(draftRef.current);
    else onCancelRef.current();
  };

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (target && rootRef.current?.contains(target)) return;
      finish(true);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, []);

  return (
    <div
      ref={rootRef}
      className="flex items-center gap-0.5 pointer-events-auto"
      data-floorplan-ui="true"
      data-wall-length-editor="true"
    >
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.preventDefault();
            finish(true);
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            finish(false);
          }
        }}
        onBlur={() => finish(true)}
        className="w-16 px-1 py-0.5 text-[13px] rounded bg-slate-900/95 border border-blue-500 text-white focus:outline-none"
      />
      <span className="text-[12px] text-blue-200 px-0.5">{unit}</span>
    </div>
  );
};

const LABEL_DRAG_THRESHOLD_PX = 4;

function pointerToCanvas(clientX: number, clientY: number): FloorPlanPoint | null {
  const layer = getPanningLayer();
  if (!layer) return null;
  const zoom = useProjectStore.getState().canvasZoom || 1;
  return clientToCanvas(clientX, clientY, layer.getBoundingClientRect(), zoom);
}

const WallLengthLabel: React.FC<{
  wall: Wall;
  lengthLabel: string;
  editorValue: string;
  unit: string;
  selected: boolean;
  hovered: boolean;
  editing: boolean;
  onSelect: (event: React.MouseEvent) => void;
  onStartEdit: () => void;
  onCommit: (raw: string) => void;
  onCancelEdit: () => void;
}> = ({
  wall,
  lengthLabel,
  editorValue,
  unit,
  selected,
  hovered,
  editing,
  onSelect,
  onStartEdit,
  onCommit,
  onCancelEdit,
}) => {
  const [liveOffset, setLiveOffset] = useState<{ along: number; out: number } | null>(null);
  const [labelHovered, setLabelHovered] = useState(false);
  const dragRef = useRef<{
    wall: Wall;
    grabDx: number;
    grabDy: number;
    startClientX: number;
    startClientY: number;
    along: number;
    out: number;
    moved: boolean;
  } | null>(null);

  const displayWall = liveOffset
    ? { ...wall, labelAlong: liveOffset.along, labelOut: liveOffset.out }
    : wall;
  const boxW = Math.max(42, lengthLabel.length * 7.5);
  const boxH = 18;
  const pos = wallLabelPosition(displayWall, boxW, boxH);

  const endDrag = (event: React.PointerEvent) => {
    const session = dragRef.current;
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* already released */
    }
    if (!session) {
      setLiveOffset(null);
      return;
    }
    if (session.moved) {
      useProjectStore.getState().updateWall(wall.id, {
        labelAlong: session.along,
        labelOut: session.out,
      });
    } else {
      onSelect(event as unknown as React.MouseEvent);
    }
    setLiveOffset(null);
  };

  if (editing) {
    return (
      <foreignObject
        x={pos.x - 50}
        y={pos.y - 16}
        width={100}
        height={32}
        style={{ pointerEvents: 'auto' }}
      >
        <WallLengthEditor value={editorValue} unit={unit} onCommit={onCommit} onCancel={onCancelEdit} />
      </foreignObject>
    );
  }

  const highlight = Boolean(liveOffset || labelHovered);
  const textFill = highlight ? '#38bdf8' : selected || hovered ? '#93c5fd' : '#64748b';

  return (
    <g data-floorplan-ui="true">
      <rect
        data-floorplan-ui="true"
        x={pos.x - boxW / 2}
        y={pos.y - boxH / 2}
        width={boxW}
        height={boxH}
        rx={3}
        fill={highlight ? 'rgba(56,189,248,0.18)' : 'transparent'}
        style={{
          pointerEvents: 'auto',
          cursor: liveOffset ? 'grabbing' : 'grab',
        }}
        onMouseEnter={() => setLabelHovered(true)}
        onMouseLeave={() => setLabelHovered(false)}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.stopPropagation();
          if (e.detail >= 2) return;
          const canvas = pointerToCanvas(e.clientX, e.clientY);
          if (!canvas) return;
          const start = wallLabelOffset(wall, boxW, boxH);
          dragRef.current = {
            wall: { ...wall, a: { ...wall.a }, b: { ...wall.b } },
            grabDx: canvas.x - pos.x,
            grabDy: canvas.y - pos.y,
            startClientX: e.clientX,
            startClientY: e.clientY,
            along: start.along,
            out: start.out,
            moved: false,
          };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const session = dragRef.current;
          if (!session || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
          e.stopPropagation();
          const dist = Math.hypot(e.clientX - session.startClientX, e.clientY - session.startClientY);
          if (!session.moved && dist < LABEL_DRAG_THRESHOLD_PX) return;
          session.moved = true;
          const canvas = pointerToCanvas(e.clientX, e.clientY);
          if (!canvas) return;
          const next = wallLabelOffsetFromPoint(
            session.wall,
            {
              x: canvas.x - session.grabDx,
              y: canvas.y - session.grabDy,
            },
            boxW,
            boxH
          );
          session.along = next.along;
          session.out = next.out;
          setLiveOffset(next);
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          dragRef.current = null;
          setLiveOffset(null);
          onSelect(e);
          onStartEdit();
        }}
      />
      <text
        data-floorplan-ui="true"
        x={pos.x}
        y={pos.y}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={textFill}
        fontSize={highlight || selected ? 13 : 12}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {lengthLabel}
      </text>
    </g>
  );
};

const FloorPlanLayerInner: React.FC<FloorPlanLayerProps> = ({
  floorPlan,
  selectedWallId,
  selectedWallIds,
  selectedRoomId,
  selectedOpeningId,
  selectedReferenceImageId = null,
  referenceImagesInteractive = false,
  liveReferenceGeometry = null,
  onReferenceImageDoubleClick,
  onReferenceImageMoveStart,
  onReferenceResizeStart,
  hoverRoomPreview,
  doorPreview,
  onSelectWall,
  onSelectRoom,
  onSelectOpening,
  onRenameRoom,
  onWallPointerDown,
  onCommitWallLength,
  showHandles = false,
  interactive = true,
}) => {
  const { walls: allWalls, openings: allOpenings, rooms: allRooms, units } = floorPlan;
  const design = useProjectStore((s) => s.getCurrentDesign());
  const referenceImages = getFloorPlanReferenceImages(floorPlan).filter((img) =>
    isVisibleOnCanvas(design, 'referenceImage', img)
  );
  const walls = allWalls.filter((w) => isVisibleOnCanvas(design, 'wall', w));
  const openings = allOpenings.filter((o) => {
    if (o.kind === 'door') return isVisibleOnCanvas(design, 'door', o);
    return !o.hidden;
  });
  const rooms = allRooms.filter((r) => isVisibleOnCanvas(design, 'room', r));
  const pixelsPerMeter = DEFAULT_PIXELS_PER_METER;
  const vertices = showHandles ? clusterVertices(walls) : [];
  const [hoveredVertexKey, setHoveredVertexKey] = useState<string | null>(null);
  const [hoveredWallId, setHoveredWallId] = useState<string | null>(null);
  const [hoveredRoomId, setHoveredRoomId] = useState<string | null>(null);
  const [editingWallId, setEditingWallId] = useState<string | null>(null);
  const selectedWallSet = new Set(
    selectedWallIds && selectedWallIds.length > 0
      ? selectedWallIds
      : selectedWallId
        ? [selectedWallId]
        : []
  );
  const appTheme = useProjectStore((s) => s.appTheme);
  const canvasBackground = useProjectStore((s) => s.canvasBackground);
  const canvasIsDark = appTheme === 'dark' || canvasBackground === 'dark';
  const idleWallStroke = canvasIsDark ? '#e8eef5' : '#334155';
  const idleVertexFill = canvasIsDark ? '#cbd5e1' : '#64748b';
  const idleVertexStroke = canvasIsDark ? '#f8fafc' : '#334155';

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 2 }}>
      {referenceImages.map((img, index) => {
        const live = liveReferenceGeometry?.id === img.id ? liveReferenceGeometry : null;
        const x = live?.x ?? img.x;
        const y = live?.y ?? img.y;
        const width = live?.width ?? img.width;
        const height = live?.height ?? img.height;
        const selected = selectedReferenceImageId === img.id;
        const canHit = referenceImagesInteractive;
        const corners: Array<'nw' | 'ne' | 'sw' | 'se'> = ['nw', 'ne', 'sw', 'se'];
        const handlePos: Record<'nw' | 'ne' | 'sw' | 'se', { left: number; top: number; cursor: string }> = {
          nw: { left: -4, top: -4, cursor: 'nwse-resize' },
          ne: { left: width - 4, top: -4, cursor: 'nesw-resize' },
          sw: { left: -4, top: height - 4, cursor: 'nesw-resize' },
          se: { left: width - 4, top: height - 4, cursor: 'nwse-resize' },
        };

        return (
          <div
            key={img.id}
            data-reference-image-id={img.id}
            className="absolute"
            style={{
              left: x,
              top: y,
              width,
              height,
              zIndex: selected ? 2 : 1,
              pointerEvents: canHit ? 'auto' : 'none',
            }}
            onDoubleClick={(e) => {
              if (!canHit) return;
              e.preventDefault();
              e.stopPropagation();
              onReferenceImageDoubleClick?.(img.id);
            }}
            onPointerDown={(e) => {
              if (!canHit || !selected) return;
              // Let resize handles handle their own pointerdown
              if ((e.target as HTMLElement).dataset?.refHandle) return;
              onReferenceImageMoveStart?.(img.id, e);
            }}
          >
            <img
              src={img.url}
              alt={img.name || `Floor plan reference ${index + 1}`}
              draggable={false}
              className="absolute inset-0 w-full h-full object-fill select-none"
              style={{
                opacity: img.opacity ?? 0.45,
                pointerEvents: 'none',
              }}
            />
            {selected && (
              <>
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    outline: '2px solid rgba(139, 92, 246, 0.95)',
                    outlineOffset: 0,
                    boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.35)',
                  }}
                />
                {corners.map((corner) => (
                  <div
                    key={corner}
                    data-ref-handle={corner}
                    className="absolute"
                    style={{
                      left: handlePos[corner].left,
                      top: handlePos[corner].top,
                      width: 8,
                      height: 8,
                      borderRadius: 1,
                      backgroundColor: '#a78bfa',
                      border: '1px solid #4c1d95',
                      cursor: handlePos[corner].cursor,
                      pointerEvents: 'auto',
                      zIndex: 3,
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onReferenceResizeStart?.(img.id, corner, e);
                    }}
                  />
                ))}
              </>
            )}
          </div>
        );
      })}

      <svg
        className="absolute top-0 left-0 overflow-visible"
        width={100000}
        height={100000}
        style={{ pointerEvents: 'none' }}
      >
        {/* Hover preview for an unclaimed closed area */}
        {hoverRoomPreview && hoverRoomPreview.polygon.length >= 3 && (
          <g pointerEvents="none">
            <polygon
              points={hoverRoomPreview.polygon.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="rgba(56, 189, 248, 0.22)"
              stroke="rgba(56, 189, 248, 0.85)"
              strokeWidth={2}
              strokeDasharray="6 4"
            />
            {(() => {
              const c = polygonCentroid(hoverRoomPreview.polygon);
              const area = roomAreaMeters(
                {
                  id: 'preview',
                  name: '',
                  polygon: hoverRoomPreview.polygon,
                  wallIds: hoverRoomPreview.wallIds,
                },
                pixelsPerMeter
              );
              const areaLabel =
                units === 'imperial'
                  ? `${(area * 10.7639).toFixed(1)} ft²`
                  : `${area.toFixed(1)} m²`;
              const label = `New room · ${areaLabel}`;
              const w = Math.max(120, label.length * 7.5);
              const h = 28;
              return (
                <foreignObject
                  x={c.x - w / 2}
                  y={c.y - h / 2}
                  width={w}
                  height={h}
                  style={{ overflow: 'visible', pointerEvents: 'none' }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#7dd3fc',
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      userSelect: 'none',
                    }}
                  >
                    {label}
                  </div>
                </foreignObject>
              );
            })()}
          </g>
        )}

        {/* Rooms */}
        {rooms.map((room) => {
          if (room.polygon.length < 3) return null;
          const points = room.polygon.map((p) => `${p.x},${p.y}`).join(' ');
          const centroid = polygonCentroid(room.polygon);
          const selected = selectedRoomId === room.id;
          const hovered = hoveredRoomId === room.id;
          const labelW = Math.max(80, room.name.length * 9 + 24);
          const labelH = 28;
          const fill = selected
            ? 'rgba(59, 130, 246, 0.18)'
            : hovered
              ? 'rgba(59, 130, 246, 0.12)'
              : 'rgba(148, 163, 184, 0.12)';
          const stroke = selected || hovered ? 'rgba(96, 165, 250, 0.9)' : 'none';
          return (
            <g key={room.id}>
              <polygon
                data-floorplan-room={room.id}
                points={points}
                fill={fill}
                stroke={stroke}
                strokeWidth={selected || hovered ? 2 : 0}
                style={{ pointerEvents: interactive ? 'auto' : 'none', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectRoom?.(room.id);
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onRenameRoom?.(room.id);
                }}
                onMouseEnter={() => setHoveredRoomId(room.id)}
                onMouseLeave={() => setHoveredRoomId((id) => (id === room.id ? null : id))}
              />
              <foreignObject
                x={centroid.x - labelW / 2}
                y={centroid.y - labelH / 2}
                width={labelW}
                height={labelH}
                style={{ overflow: 'visible', pointerEvents: 'none' }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: selected || hovered ? '#93c5fd' : '#94a3b8',
                    fontSize: 14,
                    fontWeight: 600,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    lineHeight: 1,
                  }}
                >
                  {room.name}
                </div>
              </foreignObject>
            </g>
          );
        })}

        {/* Walls with door gaps */}
        {walls.map((wall) => {
          const wallOpenings = openings.filter((o) => o.wallId === wall.id);
          const selected = selectedWallSet.has(wall.id);
          const hovered = hoveredWallId === wall.id;
          const doorTarget = doorPreview?.wallId === wall.id;
          const stroke = doorTarget
            ? doorPreview.fits
              ? '#38bdf8'
              : '#f87171'
            : selected
              ? '#60a5fa'
              : hovered
                ? '#60a5fa'
                : idleWallStroke;
          const strokeWidth = doorTarget ? 5 : selected || hovered ? 4 : 3;

          // Build segments skipping openings (+ live door preview gap)
          const segments: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
          const previewAsOpening =
            doorTarget && doorPreview.fits
              ? [
                  {
                    id: '__door-preview__',
                    wallId: wall.id,
                    t: doorPreview.t,
                    widthM: doorPreview.widthM,
                    kind: 'door' as const,
                  },
                ]
              : [];
          const sorted = [...wallOpenings, ...previewAsOpening].sort((a, b) => a.t - b.t);
          let cursorT = 0;
          const wallLen = Math.hypot(wall.b.x - wall.a.x, wall.b.y - wall.a.y);

          for (const opening of sorted) {
            const ends = openingEndpoints(wall, opening, pixelsPerMeter);
            const t0 =
              wallLen > 0
                ? Math.hypot(ends.start.x - wall.a.x, ends.start.y - wall.a.y) / wallLen
                : 0;
            const t1 =
              wallLen > 0
                ? Math.hypot(ends.end.x - wall.a.x, ends.end.y - wall.a.y) / wallLen
                : 0;
            if (t0 > cursorT) {
              segments.push({
                x1: wall.a.x + (wall.b.x - wall.a.x) * cursorT,
                y1: wall.a.y + (wall.b.y - wall.a.y) * cursorT,
                x2: wall.a.x + (wall.b.x - wall.a.x) * t0,
                y2: wall.a.y + (wall.b.y - wall.a.y) * t0,
              });
            }
            cursorT = Math.max(cursorT, t1);
          }
          if (cursorT < 1) {
            segments.push({
              x1: wall.a.x + (wall.b.x - wall.a.x) * cursorT,
              y1: wall.a.y + (wall.b.y - wall.a.y) * cursorT,
              x2: wall.b.x,
              y2: wall.b.y,
            });
          }
          if (segments.length === 0) {
            segments.push({ x1: wall.a.x, y1: wall.a.y, x2: wall.b.x, y2: wall.b.y });
          }

          const lengthLabel = formatMeters(wallLengthMeters(wall, pixelsPerMeter), units);

          return (
            <g key={wall.id}>
              {/* Invisible hit target */}
              <line
                data-wall-id={wall.id}
                x1={wall.a.x}
                y1={wall.a.y}
                x2={wall.b.x}
                y2={wall.b.y}
                stroke="transparent"
                strokeWidth={14}
                style={{
                  pointerEvents: interactive ? 'stroke' : 'none',
                  cursor: showHandles
                    ? selected
                      ? wallResizeCursor(wall)
                      : 'pointer'
                    : 'pointer',
                }}
                onPointerDown={(e) => {
                  if (!showHandles || e.button !== 0) return;
                  // Lengthen/push drag only once the wall is already selected
                  if (!selected) return;
                  e.stopPropagation();
                  onWallPointerDown?.(e, { mode: 'push', wallId: wall.id });
                }}
                onMouseDown={(e) => {
                  // Let unselected walls bubble so canvas can start a pan gesture
                  if (!showHandles || !selected) return;
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectWall?.(wall.id, e);
                }}
                onMouseEnter={() => setHoveredWallId(wall.id)}
                onMouseLeave={() => setHoveredWallId((id) => (id === wall.id ? null : id))}
              />
              {segments.map((seg, i) => (
                <line
                  key={i}
                  x1={seg.x1}
                  y1={seg.y1}
                  x2={seg.x2}
                  y2={seg.y2}
                  stroke={stroke}
                  strokeWidth={strokeWidth}
                  strokeLinecap="square"
                  style={{ pointerEvents: 'none' }}
                />
              ))}
              {!showHandles && (
                <>
                  <circle cx={wall.a.x} cy={wall.a.y} r={selected ? 4 : 3} fill={stroke} style={{ pointerEvents: 'none' }} />
                  <circle cx={wall.b.x} cy={wall.b.y} r={selected ? 4 : 3} fill={stroke} style={{ pointerEvents: 'none' }} />
                </>
              )}
              <WallLengthLabel
                wall={wall}
                lengthLabel={lengthLabel}
                editorValue={formatMetersValue(wallLengthMeters(wall, pixelsPerMeter), units)}
                unit={units === 'imperial' ? 'ft' : 'm'}
                selected={selected}
                hovered={hovered}
                editing={editingWallId === wall.id}
                onSelect={(e) => onSelectWall?.(wall.id, e)}
                onStartEdit={() => setEditingWallId(wall.id)}
                onCommit={(raw) => {
                  onCommitWallLength?.(wall.id, raw);
                  setEditingWallId(null);
                }}
                onCancelEdit={() => setEditingWallId(null)}
              />
            </g>
          );
        })}

        {showHandles &&
          vertices.map((vertex) => {
            const wallId = vertex.refs[0]?.wallId;
            if (!wallId) return null;
            const selected = vertex.refs.some((r) => selectedWallSet.has(r.wallId));
            const hovered = hoveredVertexKey === vertex.key;
            const cursor = cornerResizeCursor(vertex, walls);
            return (
              <g key={`vertex-${vertex.key}`}>
                <circle
                  data-wall-handle="vertex"
                  data-wall-id={wallId}
                  cx={vertex.point.x}
                  cy={vertex.point.y}
                  r={12}
                  fill="transparent"
                  style={{ pointerEvents: interactive ? 'auto' : 'none', cursor }}
                  onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    e.stopPropagation();
                    onWallPointerDown?.(e, {
                      mode: 'corner',
                      wallId,
                      vertexPoint: vertex.point,
                    });
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseEnter={() => setHoveredVertexKey(vertex.key)}
                  onMouseLeave={() => setHoveredVertexKey((key) => (key === vertex.key ? null : key))}
                />
                {hovered && (
                  <circle
                    cx={vertex.point.x}
                    cy={vertex.point.y}
                    r={9}
                    fill="none"
                    stroke="#60a5fa"
                    strokeWidth={2}
                    style={{ pointerEvents: 'none' }}
                  />
                )}
                <circle
                  cx={vertex.point.x}
                  cy={vertex.point.y}
                  r={hovered ? 6 : selected ? 5 : 4}
                  fill={hovered ? '#93c5fd' : selected ? '#93c5fd' : idleVertexFill}
                  stroke={hovered ? '#3b82f6' : selected ? '#1d4ed8' : idleVertexStroke}
                  strokeWidth={hovered ? 2 : 1}
                  style={{ pointerEvents: 'none' }}
                />
              </g>
            );
          })}

        {/* Door / window openings */}
        {openings.map((opening) => {
          const wall = walls.find((w) => w.id === opening.wallId);
          if (!wall) return null;
          const selected = selectedOpeningId === opening.id;
          const doorGeom =
            opening.kind === 'door'
              ? doorSwingGeometry(wall, opening, pixelsPerMeter)
              : null;
          const { start, end, center } = doorGeom ?? openingEndpoints(wall, opening, pixelsPerMeter);

          return (
            <g
              key={opening.id}
              data-floorplan-opening={opening.id}
              style={{ pointerEvents: interactive ? 'auto' : 'none', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onSelectOpening?.(opening.id);
              }}
            >
              <line
                x1={start.x}
                y1={start.y}
                x2={end.x}
                y2={end.y}
                stroke={selected ? '#fbbf24' : '#cbd5e1'}
                strokeWidth={2}
                strokeDasharray={opening.kind === 'window' ? '4 3' : undefined}
              />
              {doorGeom && (
                <>
                  <line
                    x1={doorGeom.hinge.x}
                    y1={doorGeom.hinge.y}
                    x2={doorGeom.open.x}
                    y2={doorGeom.open.y}
                    stroke={selected ? '#fbbf24' : '#94a3b8'}
                    strokeWidth={1.5}
                    strokeLinecap="round"
                  />
                  <path
                    d={`M ${doorGeom.latch.x} ${doorGeom.latch.y} A ${doorGeom.radius} ${doorGeom.radius} 0 0 ${doorGeom.sweepFlag} ${doorGeom.open.x} ${doorGeom.open.y}`}
                    fill="none"
                    stroke={selected ? '#fbbf24' : '#94a3b8'}
                    strokeWidth={1.5}
                  />
                </>
              )}
              <circle cx={center.x} cy={center.y} r={3} fill={selected ? '#fbbf24' : '#94a3b8'} />
            </g>
          );
        })}

        {/* Live door placement preview */}
        {doorPreview && (() => {
          const wall = walls.find((w) => w.id === doorPreview.wallId);
          if (!wall) return null;
          const geom = doorSwingGeometry(
            wall,
            {
              t: doorPreview.t,
              widthM: doorPreview.widthM,
              hinge: doorPreview.hinge,
              swingSide: doorPreview.swingSide,
            },
            pixelsPerMeter
          );
          const color = doorPreview.fits ? '#38bdf8' : '#f87171';

          return (
            <g style={{ pointerEvents: 'none' }}>
              {/* Soft highlight on the target wall */}
              <line
                x1={wall.a.x}
                y1={wall.a.y}
                x2={wall.b.x}
                y2={wall.b.y}
                stroke={color}
                strokeWidth={8}
                strokeOpacity={0.18}
                strokeLinecap="round"
              />
              {/* Opening span on the wall */}
              <line
                x1={geom.start.x}
                y1={geom.start.y}
                x2={geom.end.x}
                y2={geom.end.y}
                stroke={color}
                strokeWidth={3}
                strokeLinecap="butt"
              />
              {doorPreview.fits && (
                <>
                  <line
                    x1={geom.hinge.x}
                    y1={geom.hinge.y}
                    x2={geom.open.x}
                    y2={geom.open.y}
                    stroke={color}
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                  <path
                    d={`M ${geom.latch.x} ${geom.latch.y} A ${geom.radius} ${geom.radius} 0 0 ${geom.sweepFlag} ${geom.open.x} ${geom.open.y}`}
                    fill={color}
                    fillOpacity={0.12}
                    stroke={color}
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                  />
                </>
              )}
              <circle cx={geom.center.x} cy={geom.center.y} r={3.5} fill={color} />
              {!doorPreview.fits && (
                <text
                  x={geom.center.x}
                  y={geom.center.y - 12}
                  textAnchor="middle"
                  fill={color}
                  fontSize={11}
                  style={{ userSelect: 'none' }}
                >
                  Wall too short
                </text>
              )}
            </g>
          );
        })()}
      </svg>
    </div>
  );
};

const FloorPlanLayer: React.FC<FloorPlanLayerProps> = (props) => {
  const livePlan = useLiveFloorPlanStore((s) => s.plan);
  const liveHover = useLiveFloorPlanStore((s) => s.hoverRoomPreview);
  return (
    <FloorPlanLayerInner
      {...props}
      floorPlan={livePlan ?? props.floorPlan}
      hoverRoomPreview={liveHover ?? props.hoverRoomPreview}
    />
  );
};

export default React.memo(FloorPlanLayer);
